import React, { useState, useEffect } from 'react';
import { 
  FaUniversity, 
  FaPlus, 
  FaSignOutAlt, 
  FaUser, 
  FaEnvelope, 
  FaBuilding, 
  FaGraduationCap, 
  FaChartBar, 
  FaImage,
  FaSearch,
  FaFilter,
  FaDownload,
  FaEye,
  FaTrash,
  FaEdit,
  FaCheck,
  FaClock,
  FaPhone,
  FaMapMarkerAlt,
  FaFileAlt,
  FaCalendarAlt,
  FaUserGraduate,
  FaUsers,
  FaUserTie,
  FaComments,
  FaStar,
  FaTimes,
  FaDollarSign,
  FaLink,
  FaCity,
  FaGlobe,
  FaMapPin,
  FaHome,
  FaBars,
  FaBookOpen,
  FaShieldAlt
} from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { UniversityAuthService } from '../../services/UniversityAuthService';
import { UniversityOpportunityService } from '../../services/UniversityOpportunityService';
import { UniversityApplicantService } from '../../services/UniversityApplicantService';
import { useSnackBar } from '../../hooks/useSnackBar';
import UniversityOpportunityForm from './UniversityOpportunityForm';
import TipTapEditor from '../common/TipTapEditor';
import { encodeImageUrl } from '../../utils/helpers';

// Helper function to decode HTML entities
const decodeHtmlEntities = (text) => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

// Modal for viewing opportunity details
function OpportunityDetailsModal({ opportunity, onClose, loading }) {
  if (!opportunity && !loading) return null;
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString();
  };
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl shadow-2xl border border-[#FFD6D4] w-[90%] max-w-4xl max-h-[90vh] flex flex-col relative overflow-hidden ml-20">
        {/* Header with close button */}
        <div className="flex items-center justify-between p-6 border-b border-[#FFD6D4] bg-gradient-to-r from-[#FFEEED] to-white">
          <h1 className="text-2xl font-bold text-[#DB4F40]">Opportunity Details</h1>
          <button
            className="flex items-center justify-center w-10 h-10 rounded-full bg-[#DB4F40] text-white hover:bg-[#C64537] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            onClick={onClose}
            title="Close"
          >
            <FaTimes className="h-5 w-5" />
          </button>
        </div>
        
        {/* Content area with scroll */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center min-h-[400px]">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#FFD6D4] border-t-[#DB4F40]"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* University and location section */}
              <div className="flex flex-col lg:flex-row gap-6 items-start">
                <div className="flex-shrink-0 bg-white p-4 rounded-2xl shadow-lg border-2 border-[#FFD6D4] flex items-center justify-center min-w-[120px]">
                  <img
                    src={`${process.env.NODE_ENV === 'development' ? 'http://localhost:8181' : ''}/api/university/logo/${encodeURIComponent(opportunity.university)}`}
                    alt={opportunity.university}
                    className="h-24 w-24 object-contain"
                    onError={e => (e.target.style.display = 'none')}
                  />
                  {!opportunity.university && (
                    <FaUniversity className="h-24 w-24 text-[#DB4F40]/30 p-4" />
                  )}
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-bold text-[#1B222A] mb-3">{opportunity.university}</h2>
                  <div className="flex items-center text-[#1B222A]/80 space-x-3 mb-2">
                    <FaMapPin className="text-[#DB4F40] flex-shrink-0 text-lg" />
                    <span className="text-xl font-medium">{opportunity.city}, {opportunity.country}</span>
                  </div>
                  <div className="text-lg text-[#1B222A]/70 font-medium">{opportunity.department}</div>
                </div>
              </div>

              {/* Title */}
              <div className="bg-gradient-to-r from-[#FFEEED] to-white p-6 rounded-2xl border border-[#FFD6D4]">
                <h1 className="text-3xl font-bold text-[#DB4F40] leading-tight">{opportunity.title}</h1>
              </div>

              {/* Key details grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {opportunity.supervisor && (
                  <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-[#FFD6D4] hover:shadow-xl transition-shadow">
                    <div className="flex items-center mb-3">
                      <FaUserTie className="text-[#DB4F40] mr-2 text-lg" />
                      <span className="font-bold text-[#1B222A] text-lg">Supervisor</span>
                    </div>
                    <span className="text-[#1B222A]/80 text-base">{opportunity.supervisor}</span>
                  </div>
                )}
                {opportunity.closing_date && (
                  <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-[#FFD6D4] hover:shadow-xl transition-shadow">
                    <div className="flex items-center mb-3">
                      <FaCalendarAlt className="text-[#DB4F40] mr-2 text-lg" />
                      <span className="font-bold text-[#1B222A] text-lg">Deadline</span>
                    </div>
                    <span className="text-[#1B222A]/80 text-base">{formatDate(opportunity.closing_date)}</span>
                  </div>
                )}
                {opportunity.fully_funded !== undefined && (
                  <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-[#FFD6D4] hover:shadow-xl transition-shadow">
                    <div className="flex items-center mb-3">
                      <FaDollarSign className="text-[#DB4F40] mr-2 text-lg" />
                      <span className="font-bold text-[#1B222A] text-lg">Funding</span>
                    </div>
                    <span className="text-[#1B222A]/80 text-base">
                      {opportunity.fully_funded ? 'Fully Funded' : 'Partial/Not Funded'}
                    </span>
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-[#FFD6D4]">
                <h3 className="text-xl font-bold text-[#DB4F40] mb-4 flex items-center">
                  <FaFileAlt className="mr-2" />
                  Description
                </h3>
                <div className="prose prose-lg max-w-none text-[#1B222A]/90 leading-relaxed">
                  <div dangerouslySetInnerHTML={{ __html: opportunity.description }} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Edit Profile Modal Component
function EditProfileModal({ user, onClose, onSubmit }) {
  const [focusedField, setFocusedField] = useState(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    university: "",
    position: "",
    department: "",
    city: "",
    country: ""
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize form data when user changes
  useEffect(() => {
    if (user) {
      setLoading(true);
      setError(null);
      
      try {
        const newFormData = {
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          university: user.university || "",
          position: user.position || "",
          department: user.department || "",
          city: user.city || "",
          country: user.country || ""
        };
        
        setFormData(newFormData);
        setLoading(false);
      } catch (err) {
        setError('Failed to load profile data. Please try again.');
        setLoading(false);
        console.error('Error loading profile data:', err);
      }
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    setSaving(true);
    setError(null);

    try {
      await onSubmit(formData);
    } catch (err) {
      setError(err.message || 'Failed to update profile. Please try again.');
      console.error('Error updating profile:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-2xl w-full mx-4">
          <div className="flex justify-center items-center min-h-[200px]">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#FFD6D4] border-t-[#DB4F40]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl shadow-2xl border border-[#FFD6D4] w-[90%] max-w-4xl max-h-[90vh] flex flex-col relative overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#FFD6D4] bg-gradient-to-r from-[#FFEEED] to-white">
          <h1 className="text-2xl font-bold text-[#DB4F40]">Edit Profile</h1>
          <button
            className="flex items-center justify-center w-10 h-10 rounded-full bg-[#DB4F40] text-white hover:bg-[#C64537] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            onClick={onClose}
            title="Close"
          >
            <FaTimes className="h-5 w-5" />
          </button>
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#1B222A]">
                  <FaUser className="inline mr-2 text-[#DB4F40]" />
                  First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('firstName')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 ${
                    focusedField === 'firstName' 
                      ? 'border-[#DB4F40] bg-white shadow-lg' 
                      : 'border-[#FFD6D4] bg-white/60 hover:shadow-md'
                  }`}
                  placeholder="Enter first name"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#1B222A]">
                  <FaUser className="inline mr-2 text-[#DB4F40]" />
                  Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('lastName')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 ${
                    focusedField === 'lastName' 
                      ? 'border-[#DB4F40] bg-white shadow-lg' 
                      : 'border-[#FFD6D4] bg-white/60 hover:shadow-md'
                  }`}
                  placeholder="Enter last name"
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-[#1B222A]">
                <FaEnvelope className="inline mr-2 text-[#DB4F40]" />
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField(null)}
                className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 ${
                  focusedField === 'email' 
                    ? 'border-[#DB4F40] bg-white shadow-lg' 
                    : 'border-[#FFD6D4] bg-white/60 hover:shadow-md'
                }`}
                placeholder="Enter email address"
                required
              />
            </div>

            {/* University Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#1B222A]">
                  <FaUniversity className="inline mr-2 text-[#DB4F40]" />
                  University *
                </label>
                <input
                  type="text"
                  name="university"
                  value={formData.university}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('university')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 ${
                    focusedField === 'university' 
                      ? 'border-[#DB4F40] bg-white shadow-lg' 
                      : 'border-[#FFD6D4] bg-white/60 hover:shadow-md'
                  }`}
                  placeholder="University name"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#1B222A]">
                  <FaUserTie className="inline mr-2 text-[#DB4F40]" />
                  Position *
                </label>
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('position')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 ${
                    focusedField === 'position' 
                      ? 'border-[#DB4F40] bg-white shadow-lg' 
                      : 'border-[#FFD6D4] bg-white/60 hover:shadow-md'
                  }`}
                  placeholder="Your position"
                  required
                />
              </div>
            </div>

            {/* Department and Location */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#1B222A]">
                  <FaUsers className="inline mr-2 text-[#DB4F40]" />
                  Department *
                </label>
                <input
                  type="text"
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('department')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 ${
                    focusedField === 'department' 
                      ? 'border-[#DB4F40] bg-white shadow-lg' 
                      : 'border-[#FFD6D4] bg-white/60 hover:shadow-md'
                  }`}
                  placeholder="Department"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#1B222A]">
                  <FaCity className="inline mr-2 text-[#DB4F40]" />
                  City *
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('city')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 ${
                    focusedField === 'city' 
                      ? 'border-[#DB4F40] bg-white shadow-lg' 
                      : 'border-[#FFD6D4] bg-white/60 hover:shadow-md'
                  }`}
                  placeholder="City"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#1B222A]">
                  <FaGlobe className="inline mr-2 text-[#DB4F40]" />
                  Country *
                </label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('country')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 ${
                    focusedField === 'country' 
                      ? 'border-[#DB4F40] bg-white shadow-lg' 
                      : 'border-[#FFD6D4] bg-white/60 hover:shadow-md'
                  }`}
                  placeholder="Country"
                  required
                />
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-[#FFD6D4]">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 border-2 border-[#FFD6D4] text-[#DB4F40] rounded-xl hover:bg-[#FFEEED] transition-all duration-300 font-semibold"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-3 bg-[#DB4F40] text-white rounded-xl hover:bg-[#C64537] transition-all duration-300 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Updating...' : 'Update Profile'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// Edit Opportunity Modal Component
function EditOpportunityModal({ opportunity, onClose, onSubmit }) {
  const [focusedField, setFocusedField] = useState(null);
  const [formData, setFormData] = useState({
    category: "standard",
    title: "",
    department: "",
    university: "",
    university_url: "",
    city: "",
    country: "",
    closing_date: "",
    open_to: "",
    supervisor: "",
    supervisor_url: "",
    type: "PhD",
    fully_funded: false,
    description: "",
    status: "pending"
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize form data when opportunity changes
  useEffect(() => {
    if (opportunity) {
      setLoading(true);
      setError(null); // Clear any previous errors
      
      try {
        // Convert Unix timestamp to date string for the date input
        const closingDate = opportunity.closing_date 
          ? new Date(opportunity.closing_date * 1000).toISOString().split('T')[0]
          : '';
        
        const newFormData = {
          category: opportunity.category || "standard",
          title: opportunity.title || "",
          department: opportunity.department || "",
          university: opportunity.university || "",
          university_url: opportunity.university_url || "",
          city: opportunity.city || "",
          country: opportunity.country || "",
          closing_date: closingDate,
          open_to: opportunity.open_to || "",
          supervisor: opportunity.supervisor || "",
          supervisor_url: opportunity.supervisor_url || "",
          type: opportunity.type || "PhD",
          fully_funded: opportunity.fully_funded || false,
          description: opportunity.description ? decodeHtmlEntities(opportunity.description) : "",
          status: opportunity.status || "pending"
        };
        
        setFormData(newFormData);
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load opportunity data. Please try again.');
        setLoading(false);
        console.error('Error loading opportunity data:', err);
      }
    }
  }, [opportunity]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleDescriptionChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      description: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    setSaving(true);
    setError(null);

    try {
      // Convert date back to Unix timestamp and convert to string
      const closingDateTimestamp = formData.closing_date 
        ? Math.floor(new Date(formData.closing_date).getTime() / 1000)
        : null;

      const submissionData = {
        ...formData,
        closing_date: closingDateTimestamp ? closingDateTimestamp.toString() : null
      };

      await onSubmit(submissionData);
    } catch (err) {
      setError(err.message || 'Failed to update opportunity. Please try again.');
      console.error('Error updating opportunity:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-2xl w-full mx-4">
          <div className="flex justify-center items-center min-h-[200px]">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#FFD6D4] border-t-[#DB4F40]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl shadow-2xl border border-[#FFD6D4] w-[90%] max-w-4xl max-h-[90vh] flex flex-col relative overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#FFD6D4] bg-gradient-to-r from-[#FFEEED] to-white">
          <h1 className="text-2xl font-bold text-[#DB4F40]">Edit Opportunity</h1>
          <button
            className="flex items-center justify-center w-10 h-10 rounded-full bg-[#DB4F40] text-white hover:bg-[#C64537] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            onClick={onClose}
            title="Close"
          >
            <FaTimes className="h-5 w-5" />
          </button>
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {/* Title */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-[#1B222A]">
                <FaUserGraduate className="inline mr-2 text-[#DB4F40]" />
                Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                onFocus={() => setFocusedField('title')}
                onBlur={() => setFocusedField(null)}
                className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 ${
                  focusedField === 'title' 
                    ? 'border-[#DB4F40] bg-white shadow-lg' 
                    : 'border-[#FFD6D4] bg-white/60 hover:shadow-md'
                }`}
                placeholder="Enter opportunity title"
                required
              />
            </div>

            {/* University and Location */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#1B222A]">
                  <FaUniversity className="inline mr-2 text-[#DB4F40]" />
                  University *
                </label>
                <input
                  type="text"
                  name="university"
                  value={formData.university}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('university')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 ${
                    focusedField === 'university' 
                      ? 'border-[#DB4F40] bg-white shadow-lg' 
                      : 'border-[#FFD6D4] bg-white/60 hover:shadow-md'
                  }`}
                  placeholder="University name"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#1B222A]">
                  <FaCity className="inline mr-2 text-[#DB4F40]" />
                  City *
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('city')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 ${
                    focusedField === 'city' 
                      ? 'border-[#DB4F40] bg-white shadow-lg' 
                      : 'border-[#FFD6D4] bg-white/60 hover:shadow-md'
                  }`}
                  placeholder="City"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#1B222A]">
                  <FaGlobe className="inline mr-2 text-[#DB4F40]" />
                  Country *
                </label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('country')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 ${
                    focusedField === 'country' 
                      ? 'border-[#DB4F40] bg-white shadow-lg' 
                      : 'border-[#FFD6D4] bg-white/60 hover:shadow-md'
                  }`}
                  placeholder="Country"
                  required
                />
              </div>
            </div>

            {/* Department and Supervisor */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#1B222A]">
                  <FaUsers className="inline mr-2 text-[#DB4F40]" />
                  Department *
                </label>
                <input
                  type="text"
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('department')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 ${
                    focusedField === 'department' 
                      ? 'border-[#DB4F40] bg-white shadow-lg' 
                      : 'border-[#FFD6D4] bg-white/60 hover:shadow-md'
                  }`}
                  placeholder="Department"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#1B222A]">
                  <FaUserTie className="inline mr-2 text-[#DB4F40]" />
                  Supervisor *
                </label>
                <input
                  type="text"
                  name="supervisor"
                  value={formData.supervisor}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('supervisor')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 ${
                    focusedField === 'supervisor' 
                      ? 'border-[#DB4F40] bg-white shadow-lg' 
                      : 'border-[#FFD6D4] bg-white/60 hover:shadow-md'
                  }`}
                  placeholder="Supervisor name"
                  required
                />
              </div>
            </div>

            {/* URLs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#1B222A]">
                  <FaLink className="inline mr-2 text-[#DB4F40]" />
                  University URL
                </label>
                <input
                  type="url"
                  name="university_url"
                  value={formData.university_url}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('university_url')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 ${
                    focusedField === 'university_url' 
                      ? 'border-[#DB4F40] bg-white shadow-lg' 
                      : 'border-[#FFD6D4] bg-white/60 hover:shadow-md'
                  }`}
                  placeholder="https://university.edu"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#1B222A]">
                  <FaLink className="inline mr-2 text-[#DB4F40]" />
                  Supervisor URL
                </label>
                <input
                  type="url"
                  name="supervisor_url"
                  value={formData.supervisor_url}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('supervisor_url')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 ${
                    focusedField === 'supervisor_url' 
                      ? 'border-[#DB4F40] bg-white shadow-lg' 
                      : 'border-[#FFD6D4] bg-white/60 hover:shadow-md'
                  }`}
                  placeholder="https://linkedin.com/in/supervisor"
                />
              </div>
            </div>

            {/* Date and Funding */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#1B222A]">
                  <FaCalendarAlt className="inline mr-2 text-[#DB4F40]" />
                  Closing Date
                </label>
                <input
                  type="date"
                  name="closing_date"
                  value={formData.closing_date}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('closing_date')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 ${
                    focusedField === 'closing_date' 
                      ? 'border-[#DB4F40] bg-white shadow-lg' 
                      : 'border-[#FFD6D4] bg-white/60 hover:shadow-md'
                  }`}
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#1B222A]">
                  <FaDollarSign className="inline mr-2 text-[#DB4F40]" />
                  Funding Status
                </label>
                <div className="flex items-center space-x-4 mt-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="fully_funded"
                      checked={formData.fully_funded}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-[#DB4F40] border-gray-300 rounded focus:ring-[#DB4F40]"
                    />
                    <span className="ml-2 text-sm text-[#1B222A]">Fully Funded</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-[#1B222A]">
                <FaFileAlt className="inline mr-2 text-[#DB4F40]" />
                Description *
              </label>
              <div className="border-2 border-[#FFD6D4] rounded-xl overflow-hidden">
                <TipTapEditor
                  key={opportunity?.id || 'new'}
                  value={formData.description}
                  onChange={handleDescriptionChange}
                  placeholder="Enter opportunity description..."
                />
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-[#FFD6D4]">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 border-2 border-[#FFD6D4] text-[#DB4F40] rounded-xl hover:bg-[#FFEEED] transition-all duration-300 font-semibold"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-3 bg-[#DB4F40] text-white rounded-xl hover:bg-[#C64537] transition-all duration-300 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Updating...' : 'Update Opportunity'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

const UniversityDashboard = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSection, setSelectedSection] = useState('opportunities');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  
  // Upload logo state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [logoRefreshKey, setLogoRefreshKey] = useState(0);
  
  // Edit profile state
  const [editProfileModal, setEditProfileModal] = useState({ open: false, user: null });
  
  // Opportunities management state
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);
  const [filters, setFilters] = useState({
    title: '',
    department: '',
    supervisor: ''
  });
  const [pendingFilters, setPendingFilters] = useState({
    title: '',
    department: '',
    supervisor: ''
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 30
  });
  const [pendingCounts, setPendingCounts] = useState({});
  const [totalApplicantCounts, setTotalApplicantCounts] = useState({});

  // Modal and selected opportunity state for view/edit
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, opportunity: null });
  
  const navigate = useNavigate();
  const location = useLocation();
  const enqMsg = useSnackBar();

  useEffect(() => {
    loadUserData();
  }, []);

  // Touch gesture handlers for mobile sidebar
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && isSidebarOpen) {
      setIsSidebarOpen(false);
    }
    if (isRightSwipe && !isSidebarOpen) {
      setIsSidebarOpen(true);
    }
  };

  // Handle navigation state to set the correct section
  useEffect(() => {
    if (location.state?.section) {
      setSelectedSection(location.state.section);
    }
  }, [location.state]);


  const loadUserData = async () => {
    try {
      const storedUser = UniversityAuthService.getStoredUser();
      if (storedUser) {
        setUser(storedUser);
        
        // Dispatch custom event to notify header of user data
        window.dispatchEvent(new CustomEvent('universityUserUpdated', {
          detail: { user: storedUser }
        }));
        
        // Always refresh user data from server to get latest city/country
        try {
          const freshUser = await UniversityAuthService.getCurrentUser();
          console.log('Fresh user data loaded:', freshUser);
          setUser(freshUser);
          // Store fresh user data
          localStorage.setItem('university_user', JSON.stringify(freshUser));
          // Dispatch event again with fresh user data
          window.dispatchEvent(new CustomEvent('universityUserUpdated', {
            detail: { user: freshUser }
          }));
        } catch (error) {
          // If server call fails, use stored data
          console.warn('Failed to refresh user data:', error);
        }
      } else {
        // No stored user, redirect to login
        navigate('/university/login');
      }
    } catch (error) {
      enqMsg('Failed to load user data. Please log in again.', 'error');
      navigate('/university/login');
    } finally {
      setIsLoading(false);
    }
  };


  const handleLogout = async () => {
    try {
      await UniversityAuthService.logout();
      enqMsg('Logged out successfully', 'success');
      navigate('/university/login');
    } catch (error) {
      enqMsg('Logout failed. Please try again.', 'error');
    }
  };

  const handleOpportunityCreated = (opportunity) => {
    setOpportunities(prev => [opportunity, ...prev]);
    setSelectedSection('opportunities');
    enqMsg('Opportunity created successfully!', 'success');
  };

  // Load opportunities when opportunities section is active
  useEffect(() => {
    if (selectedSection === 'opportunities') {
      loadOpportunities();
    }
  }, [selectedSection, filters, pagination.page]);

  // Load opportunities data
  const loadOpportunities = async () => {
    try {
      setOpportunitiesLoading(true);
      // For now, get all opportunities and filter on frontend
      // TODO: Implement backend filtering and pagination
      const response = await UniversityOpportunityService.getOpportunities();
      
      // Filter opportunities on frontend
      let filteredOpportunities = response || [];
      
      if (filters.title) {
        filteredOpportunities = filteredOpportunities.filter(opp => 
          opp.title.toLowerCase().includes(filters.title.toLowerCase())
        );
      }
      
      if (filters.department) {
        filteredOpportunities = filteredOpportunities.filter(opp => 
          opp.department.toLowerCase().includes(filters.department.toLowerCase())
        );
      }
      
      if (filters.supervisor) {
        filteredOpportunities = filteredOpportunities.filter(opp => 
          opp.supervisor.toLowerCase().includes(filters.supervisor.toLowerCase())
        );
      }
      
      // Apply pagination
      const startIndex = (pagination.page - 1) * pagination.limit;
      const endIndex = startIndex + pagination.limit;
      const paginatedOpportunities = filteredOpportunities.slice(startIndex, endIndex);
      
      setOpportunities(paginatedOpportunities);
      setPagination(prev => ({
        ...prev,
        total: filteredOpportunities.length
      }));

      // Load applicant counts for opportunities that collect resumes
      await loadApplicantCounts(paginatedOpportunities);
    } catch (error) {
      console.error('Failed to load opportunities:', error);
      enqMsg('Failed to load opportunities. Please try again.', 'error');
      setOpportunities([]);
    } finally {
      setOpportunitiesLoading(false);
    }
  };

  // Load applicant counts for each opportunity
  const loadApplicantCounts = async (opportunitiesList) => {
    const counts = {};
    const totalCounts = {};
    
    // Only load counts for opportunities that collect resumes
    const opportunitiesWithResumes = opportunitiesList.filter(opp => opp.collect_resumes);
    
    if (opportunitiesWithResumes.length === 0) {
      setPendingCounts({});
      setTotalApplicantCounts({});
      return;
    }

    await Promise.all(
      opportunitiesWithResumes.map(async (opp) => {
        try {
          const stats = await UniversityApplicantService.getApplicantStats({ opportunity_id: opp.id });
          counts[opp.id] = stats?.status_counts?.new || 0;
          totalCounts[opp.id] = stats?.total_count || 0;
        } catch (e) {
          console.error(`Failed to load counts for opportunity ${opp.id}:`, e);
          counts[opp.id] = 0;
          totalCounts[opp.id] = 0;
        }
      })
    );
    
    setPendingCounts(counts);
    setTotalApplicantCounts(totalCounts);
  };

  // Format date helper
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString();
  };

  // View handler: fetch from backend
  const handleViewOpportunity = async (opportunity) => {
    setDetailsLoading(true);
    setShowDetailsModal(true);
    try {
      const response = await UniversityOpportunityService.getOpportunityById(opportunity.id);
      setSelectedOpportunity(response);
    } catch (error) {
      enqMsg('Failed to load opportunity details', 'error');
      setShowDetailsModal(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Delete handler: call backend
  const handleDeleteOpportunity = async (opportunity) => {
    if (!window.confirm('Are you sure you want to delete this opportunity?')) return;
    try {
      await UniversityOpportunityService.deleteOpportunity(opportunity.id);
      enqMsg('Opportunity deleted successfully', 'success');
      // Refresh list
      setOpportunities((prev) => prev.filter((o) => o.id !== opportunity.id));
    } catch (error) {
      enqMsg('Failed to delete opportunity', 'error');
    }
  };

  // Edit handler: open modal
  const handleEditOpportunity = async (opportunity) => {
    try {
      const response = await UniversityOpportunityService.getOpportunityById(opportunity.id);
      setEditModal({ open: true, opportunity: response });
    } catch (error) {
      enqMsg('Failed to load opportunity for editing', 'error');
    }
  };

  // Edit submit handler
  const handleEditSubmit = async (updatedData) => {
    try {
      await UniversityOpportunityService.updateOpportunity({
        id: editModal.opportunity.id,
        ...updatedData
      });
      setEditModal({ open: false, opportunity: null });
      enqMsg('Opportunity updated successfully', 'success');
      // Refresh the opportunities list
      loadOpportunities();
    } catch (error) {
      enqMsg('Failed to update opportunity', 'error');
      throw error;
    }
  };

  // Review applications handler
  const handleReviewOpportunity = (opportunity) => {
    // Navigate to applications review page
    navigate(`/university/applications/${opportunity.id}`);
  };

  // Upload logo handlers
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        enqMsg('Please select a valid image file', 'error');
        return;
      }
      // Validate file size (200KB max)
      if (file.size > 200 * 1024) {
        enqMsg('File size must be less than 200KB', 'error');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleLogoUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo', selectedFile);

      const response = await fetch(`${process.env.NODE_ENV === 'development' ? 'http://localhost:8181' : ''}/api/university/opportunities/upload-logo`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      enqMsg('Logo uploaded successfully!', 'success');
      setSelectedFile(null);
      
      // Force refresh the logo by updating the refresh key
      setLogoRefreshKey(prev => prev + 1);
      
      // Reset file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      console.error('Upload error:', error);
      enqMsg('Failed to upload logo. Please try again.', 'error');
    } finally {
      setUploading(false);
    }
  };

  // Profile update handler
  const handleProfileUpdate = async (updatedData) => {
    try {
      const response = await UniversityAuthService.updateProfile(updatedData);
      setUser(response);
      // Update stored user data
      localStorage.setItem('university_user', JSON.stringify(response));
      // Dispatch event to notify header of user data update
      window.dispatchEvent(new CustomEvent('universityUserUpdated', {
        detail: { user: response }
      }));
      setEditProfileModal({ open: false, user: null });
      enqMsg('Profile updated successfully!', 'success');
    } catch (error) {
      enqMsg('Failed to update profile. Please try again.', 'error');
      throw error;
    }
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user?.email || 'User';
  };

  // Get user initials
  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    }
    return user?.email?.charAt(0)?.toUpperCase() || 'U';
  };

  const menuItems = [
    {
      id: 'opportunities',
      label: 'My Opportunities',
      icon: FaGraduationCap,
      description: 'Manage PhD positions'
    },
    {
      id: 'create',
      label: 'Create Opportunity',
      icon: FaPlus,
      description: 'Post new PhD position'
    },
    {
      id: 'upload-logo',
      label: 'Upload Logo',
      icon: FaImage,
      description: 'Upload university logo'
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: FaUser,
      description: 'Account information'
    },
  ];

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FFEEED] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#DB4F40] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#1B222A]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null; // Will redirect to login
  }

  const Sidebar = () => (
    <aside
      className={`
        w-full h-full
        bg-white/98 backdrop-blur-sm shadow-2xl flex flex-col
        border-r border-[#FFD6D4] rounded-r-2xl
      `}
      style={{ maxWidth: '100vw' }}
    >
    
      {/* Close Button for Mobile */}
      <div className="md:hidden p-4 border-b border-[#FFD6D4] bg-white/95">
        <button
          onClick={() => setIsSidebarOpen(false)}
          className="flex items-center justify-center w-10 h-10 rounded-full bg-[#DB4F40] text-white hover:bg-[#C64537] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 ml-auto"
          title="Close Menu"
        >
          <FaTimes className="h-5 w-5" />
        </button>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 p-4 bg-white/90 overflow-y-auto">
        <div className="space-y-3">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => {
                  setSelectedSection(item.id);
                  setIsSidebarOpen(false);
                }}
                className={`w-full flex items-center p-4 rounded-xl transition-all duration-200 group text-base gap-3
                  ${selectedSection === item.id
                    ? 'bg-[#FFD6D4] text-[#DB4F40] border-2 border-[#DB4F40]/30 shadow-lg'
                    : 'text-[#1B222A] hover:bg-[#FFEEED] hover:text-[#DB4F40] hover:shadow-md'}
                `}
                style={{ minHeight: '4rem' }}
              >
                <Icon className={`w-6 h-6 ${
                  selectedSection === item.id ? 'text-[#DB4F40]' : 'text-[#1B222A]/60 group-hover:text-[#DB4F40]'
                }`} />
                <div className="text-left flex-1">
                  <div className="font-semibold text-lg">{item.label}</div>
                  <div className="text-sm text-[#1B222A]/70 mt-1">{item.description}</div>
                </div>
              </button>
            );
          })}
        </div>
      </nav>
      
      {/* User Info & Logout */}
      <div className="p-4 border-t border-[#FFD6D4] bg-white/95 backdrop-blur-sm flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-gradient-to-r from-[#DB4F40] to-[#c13426] rounded-full flex items-center justify-center mr-3 shadow-lg">
              <span className="text-white font-bold text-lg">
                {getUserInitials()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[#1B222A] truncate text-base">{getUserDisplayName()}</p>
              <p className="text-sm text-[#1B222A]/70 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="text-[#1B222A]/60 hover:text-[#DB4F40] transition-colors p-3 rounded-full focus:outline-none focus:ring-2 focus:ring-[#DB4F40] hover:bg-[#FFEEED]"
            title="Logout"
          >
            <FaSignOutAlt className="w-5 h-5" />
          </button>
        </div>
      </div>
    </aside>
  );


  return (
    <div 
      className="min-h-screen bg-[#FFEEED] flex"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Floating background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#FFD6D4] rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse"></div>
        <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-[#FFEEED] rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-3/4 w-80 h-80 bg-[#FFD6D4] rounded-full mix-blend-multiply filter blur-xl opacity-25 animate-pulse delay-500"></div>
      </div>

      {/* Sidebar - Only visible on desktop */}
      <div className="hidden md:block">
        <Sidebar />
      </div>
      
      {/* Mobile Sidebar - Overlay */}
      {isSidebarOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden animate-in fade-in duration-200"
            onClick={() => setIsSidebarOpen(false)}
          />
          <div className="fixed top-0 left-0 w-72 sm:w-80 h-full z-40 md:hidden animate-in slide-in-from-left duration-300">
            <Sidebar />
          </div>
        </>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        <main className="flex-1 p-4 md:p-6 overflow-y-auto bg-[#FFEEED]/30">
          <div className="relative z-10">
            
            
            {/* Floating Menu Button for Mobile */}
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="fixed bottom-6 right-6 z-50 md:hidden bg-[#DB4F40] text-white p-4 rounded-full shadow-2xl hover:bg-[#C64537] transition-all duration-300 hover:scale-110 active:scale-95 group"
              title="Open Menu"
            >
              <FaBars className="w-6 h-6 group-hover:rotate-90 transition-transform duration-300" />
            </button>

            {selectedSection === 'opportunities' && (
              <div className="min-h-screen bg-[#FFEEED] py-8 px-4 sm:px-6 lg:px-8">
                <div>
                  <div className="max-w-6xl mx-auto mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <h1 className="text-3xl font-extrabold text-[#DB4F40] mb-2">My Opportunities</h1>
                        <p className="text-gray-600">Manage and track your PhD positions</p>
                      </div>
                      <button
                        onClick={() => setSelectedSection('create')}
                        className="flex items-center px-6 py-3 bg-[#DB4F40] text-white rounded-xl hover:bg-[#C64537] transition-all duration-300 hover:shadow-lg font-semibold"
                      >
                        <FaPlus className="h-5 w-5 mr-2" />
                        Create New Opportunity
                      </button>
                    </div>
                  </div>
                  
                  <div className="relative max-w-6xl mx-auto">
                    <div className="p-8 sm:p-12">
                      {/* Filter Section */}
                      <div className="max-w-6xl mx-auto">
                        <div className="border border-[#FFD6D4] overflow-hidden mb-8 rounded-3xl bg-white">
                          <div className="p-8 sm:p-12">
                            <h2 className="text-2xl font-bold text-[#DB4F40] mb-6">Filter Opportunities</h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                              <div>
                                <label className="block text-sm font-semibold text-[#1B222A] mb-2">Title</label>
                                <div className="relative">
                                  <FaUserGraduate className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#DB4F40] w-4 h-4" />
                                  <input 
                                    type="text" 
                                    name="title" 
                                    value={pendingFilters.title} 
                                    onChange={e => setPendingFilters(f => ({ ...f, title: e.target.value }))} 
                                    placeholder="Filter by Title" 
                                    className="w-full pl-10 pr-4 py-2 bg-white/60 border-2 border-[#FFD6D4] rounded-xl focus:border-[#DB4F40] focus:bg-white transition-all duration-300 placeholder-[#DB4F40]/60 shadow-sm hover:shadow-md" 
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-semibold text-[#1B222A] mb-2">Department</label>
                                <div className="relative">
                                  <FaUsers className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#DB4F40] w-4 h-4" />
                                  <input 
                                    type="text" 
                                    name="department" 
                                    value={pendingFilters.department} 
                                    onChange={e => setPendingFilters(f => ({ ...f, department: e.target.value }))} 
                                    placeholder="Filter by Department" 
                                    className="w-full pl-10 pr-4 py-2 bg-white/60 border-2 border-[#FFD6D4] rounded-xl focus:border-[#DB4F40] focus:bg-white transition-all duration-300 placeholder-[#DB4F40]/60 shadow-sm hover:shadow-md" 
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-semibold text-[#1B222A] mb-2">Supervisor</label>
                                <div className="relative">
                                  <FaUserTie className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#DB4F40] w-4 h-4" />
                                  <input 
                                    type="text" 
                                    name="supervisor" 
                                    value={pendingFilters.supervisor} 
                                    onChange={e => setPendingFilters(f => ({ ...f, supervisor: e.target.value }))} 
                                    placeholder="Filter by Supervisor" 
                                    className="w-full pl-10 pr-4 py-2 bg-white/60 border-2 border-[#FFD6D4] rounded-xl focus:border-[#DB4F40] focus:bg-white transition-all duration-300 placeholder-[#DB4F40]/60 shadow-sm hover:shadow-md" 
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="flex justify-end mt-2">
                              <button
                                onClick={() => {
                                  setFilters({ ...pendingFilters });
                                  setPagination(p => ({ ...p, page: 1 }));
                                }}
                                className="px-6 py-2 rounded-lg bg-[#DB4F40] text-white font-semibold hover:bg-[#C64537] transition-all shadow-sm mr-2"
                                type="button"
                              >
                                Search
                              </button>
                              <button
                                onClick={() => {
                                  setPendingFilters({ title: '', department: '', supervisor: '' });
                                  setFilters({ title: '', department: '', supervisor: '' });
                                  setPagination(p => ({ ...p, page: 1 }));
                                }}
                                className="px-6 py-2 rounded-lg bg-[#DB4F40] text-white font-semibold hover:bg-[#C64537] transition-all shadow-sm"
                                type="button"
                              >
                                Reset
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Opportunities List Section - Card/Grid Layout */}
                        <div className="overflow-hidden rounded-3xl border border-0 md:border md:border-[#FFD6D4] bg-white mt-2 mb-2 sm:mt-4 sm:mb-4">
                          <div className="p-2 sm:p-8 lg:p-12">
                            <h2 className="text-2xl font-bold text-[#DB4F40] mb-6">My Opportunities</h2>
                            
                            {opportunitiesLoading ? (
                              <div className="flex items-center justify-center min-h-[400px]">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#DB4F40]"></div>
                              </div>
                            ) : opportunities.length === 0 ? (
                              <div className="text-center py-16 bg-white rounded-2xl shadow-lg border border-gray-100">
                                <div className="w-24 h-24 bg-[#DB4F40]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                  <FaGraduationCap className="h-12 w-12 text-[#DB4F40]" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-3">No opportunities yet</h3>
                                <p className="text-gray-600 mb-8 text-lg max-w-md mx-auto">Get started by creating your first PhD opportunity and attract top candidates to your university.</p>
                                <button
                                  onClick={() => setSelectedSection('create')}
                                  className="px-8 py-4 bg-[#DB4F40] text-white rounded-xl hover:bg-[#C64537] transition-all duration-300 hover:shadow-lg font-semibold text-lg"
                                >
                                  Create Your First Opportunity
                                </button>
                              </div>
                            ) : (
                              <div className="grid grid-cols-1 gap-8">
                                {opportunities.map((opportunity) => (
                                  <div key={opportunity.id} className="relative">
                                    <div
                                      className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 group mx-0 md:mx-[5%] relative border-0 md:border-4 md:hover:border-[#DB4F40] md:border-[#FFD6D4] mt-4 mb-4 sm:mt-6 sm:mb-6"
                                    >
                                      <div className="p-4 sm:p-6 flex flex-col md:flex-row gap-6">
                                        {/* Main Content */}
                                        <div className="flex-1 min-w-0 flex flex-col gap-2">
                                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                                            <h3 className="text-lg font-bold text-[#1B222A] hover:text-[#DB4F40] transition-colors cursor-pointer group-hover:text-[#DB4F40]">
                                              {opportunity.title}
                                            </h3>
                                            {opportunity.fully_funded && (
                                              <div className="flex flex-wrap gap-2">
                                                <span className="px-3 py-1 bg-[#5AC1E4]/20 text-[#1B222A] rounded-full text-xs font-medium">
                                                  Fully Funded
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-base">
                                            <div className="flex items-center">
                                              <FaGraduationCap className="text-[#DB4F40] mr-2" />
                                              <span className="font-semibold text-[#6C757D]">{opportunity.department}</span>
                                            </div>
                                            <div className="flex items-center">
                                              <FaUserTie className="text-[#DB4F40] mr-2" />
                                              <span className="font-semibold text-[#6C757D]">{opportunity.supervisor || 'N/A'}</span>
                                            </div>
                                          </div>
                                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-base">
                                            <div className="flex items-center">
                                              <FaCalendarAlt className="text-[#DB4F40] mr-2" />
                                              <span className="font-semibold text-[#6C757D]">{formatDate(opportunity.closing_date)}</span>
                                            </div>
                                            {opportunity.collect_resumes && (
                                              <>
                                                <div className="flex items-center">
                                                  <FaUser className="text-green-600 mr-2" />
                                                  <span className="font-semibold text-green-800">Total Applicants: {totalApplicantCounts[opportunity.id] ?? 0}</span>
                                                </div>
                                                <div className="flex items-center">
                                                  <FaUser className="text-blue-600 mr-2" />
                                                  <span className="font-semibold text-blue-800">Pending Reviews: {pendingCounts[opportunity.id] ?? 0}</span>
                                                </div>
                                              </>
                                            )}
                                          </div>
                                          {opportunity.ai_summary && (
                                            <div className="mt-2 text-[#1B222A]/80 text-base leading-relaxed">
                                              {opportunity.ai_summary}
                                            </div>
                                          )}
                                          <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-4">
                                            {opportunity.collect_resumes && (
                                              <button
                                                className="flex items-center px-4 py-2 bg-[#FFD6D4] text-[#DB4F40] rounded-lg hover:bg-[#FFEEED] transition-colors text-sm font-medium"
                                                onClick={() => handleReviewOpportunity(opportunity)}
                                                title="Review Applications"
                                              >
                                                <FaEye className="w-4 h-4 mr-2" />
                                                Review Applications
                                              </button>
                                            )}
                                            <button
                                              className="flex items-center px-4 py-2 bg-[#FFD6D4] text-[#DB4F40] rounded-lg hover:bg-[#FFEEED] transition-colors text-sm font-medium"
                                              onClick={() => handleViewOpportunity(opportunity)}
                                              title="View Opportunity"
                                            >
                                              <FaEye className="w-4 h-4 mr-2" />
                                              View
                                            </button>
                                            <button
                                              className="flex items-center px-4 py-2 bg-[#FFD6D4] text-[#DB4F40] rounded-lg hover:bg-[#FFEEED] transition-colors text-sm font-medium"
                                              onClick={() => handleEditOpportunity(opportunity)}
                                              title="Edit Opportunity"
                                            >
                                              <FaEdit className="w-4 h-4 mr-2" />
                                              Edit
                                            </button>
                                            <button
                                              className="flex items-center px-4 py-2 bg-[#FFD6D4] text-[#DB4F40] rounded-lg hover:bg-[#FFEEED] transition-colors text-sm font-medium"
                                              onClick={() => handleDeleteOpportunity(opportunity)}
                                              title="Delete Opportunity"
                                            >
                                              <FaTrash className="w-4 h-4 mr-2" />
                                              Delete
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Pagination Controls */}
                            {pagination.total > pagination.limit && (
                              <div className="flex flex-col sm:flex-row justify-center items-center gap-4 py-8">
                                <button
                                  onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))}
                                  disabled={pagination.page === 1}
                                  className="px-6 py-2 rounded-lg bg-white border border-[#FFD6D4] hover:border-[#DB4F40] text-[#DB4F40] hover:bg-[#FFEEED] disabled:opacity-50 transition-all"
                                >
                                  Previous
                                </button>
                                <span className="px-6 py-2 bg-[#DB4F40] text-white rounded-lg shadow-sm">
                                  Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
                                </span>
                                <button
                                  onClick={() => setPagination(p => ({ ...p, page: Math.min(Math.ceil(pagination.total / pagination.limit), p.page + 1) }))}
                                  disabled={pagination.page === Math.ceil(pagination.total / pagination.limit)}
                                  className="px-6 py-2 rounded-lg bg-white border border-[#FFD6D4] hover:border-[#DB4F40] text-[#DB4F40] hover:bg-[#FFEEED] disabled:opacity-50 transition-all"
                                >
                                  Next
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {selectedSection === 'upload-logo' && (
              <div className="min-h-screen bg-[#FFEEED] py-8 px-4 sm:px-6 lg:px-8">
                <div className="max-w-6xl mx-auto">
                  <div className="mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <h1 className="text-3xl font-extrabold text-[#DB4F40] mb-2">Upload University Logo</h1>
                        <p className="text-gray-600">Upload and manage your university logo</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-8">
                    <div className="bg-white rounded-3xl shadow-lg border border-[#FFD6D4] p-8">
                      <div className="space-y-6">
                        {/* Current Logo Display */}
                        <div className="text-center">
                          <h3 className="text-xl font-bold text-[#DB4F40] mb-4">Current Logo</h3>
                          <div className="flex justify-center">
                            <div className="w-32 h-32 border-2 border-[#FFD6D4] rounded-2xl flex items-center justify-center bg-gray-50">
                              <img
                                key={logoRefreshKey}
                                src={`${process.env.NODE_ENV === 'development' ? 'http://localhost:8181' : ''}/api/university/logo/${encodeURIComponent(user.university)}?v=${logoRefreshKey}&t=${Date.now()}`}
                                alt="University Logo"
                                className="max-w-full max-h-full object-contain"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <div className="hidden flex-col items-center justify-center text-gray-400">
                                <FaImage className="h-12 w-12 mb-2" />
                                <span className="text-sm">No Logo</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Upload Form */}
                        <div className="border-t border-[#FFD6D4] pt-6">
                          <h3 className="text-xl font-bold text-[#DB4F40] mb-4">Upload New Logo</h3>
                          <form onSubmit={handleLogoUpload} className="space-y-4">
                            <div>
                              <label className="block text-sm font-semibold text-[#1B222A] mb-2">
                                <FaImage className="inline mr-2 text-[#DB4F40]" />
                                Select Logo File
                              </label>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                className="w-full px-4 py-3 border-2 border-[#FFD6D4] rounded-xl focus:border-[#DB4F40] focus:bg-white transition-all duration-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#FFD6D4] file:text-[#DB4F40] hover:file:bg-[#FFEEED]"
                                required
                              />
                              <p className="text-sm text-gray-500 mt-2">
                                Supported formats: PNG, JPG, JPEG, WEBP, GIF, SVG. Max size: 200KB
                              </p>
                            </div>

                            {selectedFile && (
                              <div className="mt-4">
                                <h4 className="text-sm font-semibold text-[#1B222A] mb-2">Preview:</h4>
                                <div className="w-32 h-32 border-2 border-[#FFD6D4] rounded-2xl flex items-center justify-center bg-gray-50">
                                  <img
                                    src={URL.createObjectURL(selectedFile)}
                                    alt="Preview"
                                    className="max-w-full max-h-full object-contain"
                                  />
                                </div>
                              </div>
                            )}

                            <div className="flex justify-end space-x-4 pt-4">
                              <button
                                type="button"
                                onClick={() => setSelectedFile(null)}
                                className="px-6 py-3 border-2 border-[#FFD6D4] text-[#DB4F40] rounded-xl hover:bg-[#FFEEED] transition-all duration-300 font-semibold"
                              >
                                Cancel
                              </button>
                              <button
                                type="submit"
                                disabled={uploading || !selectedFile}
                                className="px-6 py-3 bg-[#DB4F40] text-white rounded-xl hover:bg-[#C64537] transition-all duration-300 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {uploading ? 'Uploading...' : 'Upload Logo'}
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {selectedSection === 'create' && (
              <div className="min-h-screen bg-[#FFEEED] py-8 px-4 sm:px-6 lg:px-8">
                <div className="max-w-6xl mx-auto">
                  <div className="mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <h1 className="text-3xl font-extrabold text-[#DB4F40] mb-2">Create New Opportunity</h1>
                        <p className="text-gray-600">Post a new PhD position and attract top candidates</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-8">
                    {user ? (
                      <div className="bg-white rounded-3xl shadow-lg border border-[#FFD6D4] p-8">
                        <UniversityOpportunityForm 
                          key={`${user.id}-${user.city}-${user.country}`} // Force re-render when user data changes
                          onSuccess={handleOpportunityCreated}
                          user={user}
                        />
                      </div>
                    ) : (
                      <div className="bg-white rounded-3xl shadow-lg border border-[#FFD6D4] p-8">
                        <div className="flex items-center justify-center min-h-[400px]">
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#DB4F40] mx-auto mb-4"></div>
                            <p className="text-gray-600">Loading user data...</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {selectedSection === 'profile' && (
              <div className="min-h-screen bg-[#FFEEED] py-8 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                  {/* Header Section */}
                  <div className="mb-8">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                      <div className="flex items-center space-x-6">
                        {/* Profile Avatar */}
                        <div className="relative">
                          <div className="w-20 h-20 bg-gradient-to-r from-[#DB4F40] to-[#C64537] rounded-2xl flex items-center justify-center shadow-lg">
                            <span className="text-white text-2xl font-bold">
                              {getUserInitials()}
                            </span>
                          </div>
                          <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                            <FaCheck className="text-white text-xs" />
                          </div>
                        </div>
                        
                        {/* User Info */}
                        <div>
                          <h1 className="text-3xl font-extrabold text-[#DB4F40] mb-2">
                            {getUserDisplayName()}
                          </h1>
                          <p className="text-lg text-gray-600 mb-1">{user.position}</p>
                          <p className="text-base text-gray-500">{user.university}</p>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => setEditProfileModal({ open: true, user: user })}
                        className="flex items-center px-6 py-3 bg-[#DB4F40] text-white rounded-xl hover:bg-[#C64537] transition-all duration-300 hover:shadow-lg font-semibold group"
                      >
                        <FaEdit className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform" />
                        Edit Profile
                      </button>
                    </div>
                  </div>
                  
                  {/* Profile Cards Grid */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Personal Information Card */}
                    <div className="bg-white rounded-3xl shadow-lg border border-[#FFD6D4] overflow-hidden">
                      <div className="bg-gradient-to-r from-[#FFD6D4] to-[#FFEEED] px-6 py-4 border-b border-[#FFD6D4]">
                        <h2 className="text-xl font-bold text-[#DB4F40] flex items-center">
                          <FaUser className="mr-3" />
                          Personal Information
                        </h2>
                      </div>
                      <div className="p-6 space-y-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-[#1B222A] uppercase tracking-wide">First Name</label>
                            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                              <p className="text-lg text-[#DB4F40] font-medium">{user.firstName}</p>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-[#1B222A] uppercase tracking-wide">Last Name</label>
                            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                              <p className="text-lg text-[#DB4F40] font-medium">{user.lastName}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <label className="block text-sm font-semibold text-[#1B222A] uppercase tracking-wide">Email Address</label>
                          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 flex items-center">
                            <FaEnvelope className="text-[#DB4F40] mr-3" />
                            <p className="text-lg text-[#DB4F40] font-medium">{user.email}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Professional Information Card */}
                    <div className="bg-white rounded-3xl shadow-lg border border-[#FFD6D4] overflow-hidden">
                      <div className="bg-gradient-to-r from-[#FFD6D4] to-[#FFEEED] px-6 py-4 border-b border-[#FFD6D4]">
                        <h2 className="text-xl font-bold text-[#DB4F40] flex items-center">
                          <FaUniversity className="mr-3" />
                          Professional Information
                        </h2>
                      </div>
                      <div className="p-6 space-y-6">
                        <div className="space-y-2">
                          <label className="block text-sm font-semibold text-[#1B222A] uppercase tracking-wide">University</label>
                          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 flex items-center">
                            <FaUniversity className="text-[#DB4F40] mr-3" />
                            <p className="text-lg text-[#DB4F40] font-medium">{user.university}</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-[#1B222A] uppercase tracking-wide">Position</label>
                            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                              <p className="text-lg text-[#DB4F40] font-medium">{user.position}</p>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-[#1B222A] uppercase tracking-wide">Department</label>
                            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                              <p className="text-lg text-[#DB4F40] font-medium">{user.department}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Location Information Card */}
                    <div className="bg-white rounded-3xl shadow-lg border border-[#FFD6D4] overflow-hidden">
                      <div className="bg-gradient-to-r from-[#FFD6D4] to-[#FFEEED] px-6 py-4 border-b border-[#FFD6D4]">
                        <h2 className="text-xl font-bold text-[#DB4F40] flex items-center">
                          <FaMapMarkerAlt className="mr-3" />
                          Location Information
                        </h2>
                      </div>
                      <div className="p-6 space-y-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-[#1B222A] uppercase tracking-wide">City</label>
                            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 flex items-center">
                              <FaCity className="text-[#DB4F40] mr-3" />
                              <p className="text-lg text-[#DB4F40] font-medium">{user.city || 'Not specified'}</p>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-[#1B222A] uppercase tracking-wide">Country</label>
                            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 flex items-center">
                              <FaGlobe className="text-[#DB4F40] mr-3" />
                              <p className="text-lg text-[#DB4F40] font-medium">{user.country || 'Not specified'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Account Status Card */}
                    <div className="bg-white rounded-3xl shadow-lg border border-[#FFD6D4] overflow-hidden">
                      <div className="bg-gradient-to-r from-[#FFD6D4] to-[#FFEEED] px-6 py-4 border-b border-[#FFD6D4]">
                        <h2 className="text-xl font-bold text-[#DB4F40] flex items-center">
                          <FaShieldAlt className="mr-3" />
                          Account Status
                        </h2>
                      </div>
                      <div className="p-6 space-y-6">
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className={`w-3 h-3 rounded-full mr-3 ${
                                user.emailVerified ? 'bg-green-500' : 'bg-yellow-500'
                              }`}></div>
                              <span className="text-lg font-medium text-[#1B222A]">Email Verification</span>
                            </div>
                            <span className={`px-4 py-2 text-sm font-semibold rounded-full ${
                              user.emailVerified 
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {user.emailVerified ? 'Verified' : 'Pending'}
                            </span>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className="w-3 h-3 rounded-full mr-3 bg-green-500"></div>
                              <span className="text-lg font-medium text-[#1B222A]">Account Status</span>
                            </div>
                            <span className="px-4 py-2 text-sm font-semibold rounded-full bg-green-100 text-green-800">
                              Active
                            </span>
                          </div>
                        </div>
                        
                        {!user.emailVerified && (
                          <div className="pt-4 border-t border-gray-200">
                            <button className="w-full px-4 py-3 text-sm font-medium text-[#DB4F40] hover:bg-[#FFD6D4] rounded-xl transition-colors duration-200 border border-[#FFD6D4]">
                              Resend Verification Email
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}


          </div>
        </main>
      </div>
      
      {/* View Opportunity Modal */}
      {showDetailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-[#1B222A]">Opportunity Details</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            
            {detailsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#DB4F40]"></div>
              </div>
            ) : selectedOpportunity ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-lg font-semibold text-[#1B222A] mb-2">Title</h4>
                    <p className="text-gray-700">{selectedOpportunity.title}</p>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[#1B222A] mb-2">University</h4>
                    <p className="text-gray-700">{selectedOpportunity.university}</p>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[#1B222A] mb-2">Department</h4>
                    <p className="text-gray-700">{selectedOpportunity.department}</p>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[#1B222A] mb-2">Supervisor</h4>
                    <p className="text-gray-700">{selectedOpportunity.supervisor || 'N/A'}</p>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[#1B222A] mb-2">Location</h4>
                    <p className="text-gray-700">{selectedOpportunity.city}, {selectedOpportunity.country}</p>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[#1B222A] mb-2">Closing Date</h4>
                    <p className="text-gray-700">{formatDate(selectedOpportunity.closing_date)}</p>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[#1B222A] mb-2">Status</h4>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      selectedOpportunity.status === 'active' 
                        ? 'bg-green-100 text-green-800'
                        : selectedOpportunity.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedOpportunity.status}
                    </span>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[#1B222A] mb-2">Funding</h4>
                    <p className="text-gray-700">{selectedOpportunity.fully_funded ? 'Fully Funded' : 'Partial/Not Funded'}</p>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-lg font-semibold text-[#1B222A] mb-2">Description</h4>
                  <p className="text-gray-700 leading-relaxed">{selectedOpportunity.description}</p>
                </div>
                
                {selectedOpportunity.opportunity_url && (
                  <div>
                    <h4 className="text-lg font-semibold text-[#1B222A] mb-2">Opportunity URL</h4>
                    <a 
                      href={selectedOpportunity.opportunity_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#DB4F40] hover:underline"
                    >
                      {selectedOpportunity.opportunity_url}
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500">No opportunity data available</p>
            )}
          </div>
        </div>
      )}
      
      {/* View Opportunity Modal */}
      {showDetailsModal && (
        <OpportunityDetailsModal
          opportunity={selectedOpportunity}
          loading={detailsLoading}
          onClose={() => setShowDetailsModal(false)}
        />
      )}
      
      {/* Edit Opportunity Modal */}
      {editModal.open && (
        <EditOpportunityModal
          opportunity={editModal.opportunity}
          onClose={() => setEditModal({ open: false, opportunity: null })}
          onSubmit={handleEditSubmit}
        />
      )}
      
      {/* Edit Profile Modal */}
      {editProfileModal.open && (
        <EditProfileModal
          user={editProfileModal.user}
          onClose={() => setEditProfileModal({ open: false, user: null })}
          onSubmit={handleProfileUpdate}
        />
      )}
    </div>
  );
};

export default UniversityDashboard;
